import React, { createContext, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useLocation, matchPath } from 'react-router'
import { observer } from 'mobx-react'
import { useService } from '@evertel/di'
import { UnreadCountsState } from '@evertel/blue-user'

interface RouteConfig {
    path: string
    name?: string
    children?: RouteConfig[]
}

interface DocumentTitleContextValue {
    setTitleSuffix: (suffix: string | undefined) => void
}

const DocumentTitleContext = createContext<DocumentTitleContextValue>({
    setTitleSuffix: () => { /** noop */ }
})

export const useDocumentTitleSuffix = () => useContext(DocumentTitleContext)

interface DocumentTitleProviderProps {
    children: React.ReactNode
    routes: RouteConfig[]
    appName?: string
}

export const DocumentTitleProvider: React.FC<DocumentTitleProviderProps> = observer(({
    children,
    routes,
    appName = 'Evertel'
}) => {
    const location = useLocation()
    const [titleSuffix, setTitleSuffix] = useState<string | undefined>()

    const unreadState = useService(UnreadCountsState, [])
    const totalUnreads = unreadState.totalUnreadCount

    // Find matching route (check children too)
    const findMatchingRoute = (): RouteConfig | undefined => {
        for (const route of routes) {
            if (matchPath(route.path, location.pathname)) {
                return route
            }
            // Check children
            if (route.children) {
                for (const child of route.children) {
                    const fullPath = `${route.path}/${child.path}`.replace(/\/+/g, '/')
                    if (matchPath(fullPath, location.pathname)) {
                        return child.name ? child : route
                    }
                }
            }
        }
        return undefined
    }

    const matchedRoute = findMatchingRoute()
    const routeName = matchedRoute?.name

    useEffect(() => {
        const unreadPart = totalUnreads ? `(${totalUnreads})` : ''
        const basePart = `${appName} ${unreadPart}`.trim()

        // Build: "Evertel (23) | Room - #123" or "Evertel (23) | Room" or "Evertel (23)"
        const namePart = routeName
            ? (titleSuffix ? `${routeName} - ${titleSuffix}` : routeName)
            : undefined

        document.title = namePart ? `${basePart} | ${namePart}` : basePart
    }, [totalUnreads, routeName, titleSuffix, appName])

    // Clear suffix on route change
    useLayoutEffect(() => {
        setTitleSuffix(undefined)
    }, [routeName])

    // Memoize context value to prevent consumer re-renders when
    // observer() triggers from unread count poll updates
    const contextValue = useMemo(() => ({ setTitleSuffix }), [])

    return (
        <DocumentTitleContext.Provider value={contextValue}>
            {children}
        </DocumentTitleContext.Provider>
    )
})
